import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Alert,
  App,
  Progress,
  Tag,
  Table,
  Input,
  Divider,
  Empty,
  Descriptions,
} from 'antd';
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  HistoryOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  submitPreseed,
  cancelPreseedJob,
  listPreseedJobs,
  type PreseedProduct,
  type PreseedJobSummary,
} from '../api/preseed';

const { Title, Text, Paragraph } = Typography;

const POLL_INTERVAL = 2000;

function getStatusColor(status: string) {
  switch (status) {
    case 'completed': return 'green';
    case 'failed': return 'red';
    case 'cancelled': return 'default';
    case 'processing': return 'blue';
    case 'queued': return 'orange';
    default: return 'default';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed': return <CheckCircleOutlined />;
    case 'failed': return <CloseCircleOutlined />;
    case 'cancelled': return <CloseCircleOutlined />;
    case 'processing':
    case 'queued': return <LoadingOutlined spin />;
    default: return null;
  }
}

export function PreseedPage() {
  const { message } = App.useApp();
  const [products, setProducts] = useState<PreseedProduct[]>([{ mpn: '', manufacturer: '' }]);
  const [bulkText, setBulkText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeJobs, setActiveJobs] = useState<PreseedJobSummary[]>([]);
  const [jobHistory, setJobHistory] = useState<PreseedJobSummary[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [cancellingJobs, setCancellingJobs] = useState<Set<number>>(new Set());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      const jobs = await listPreseedJobs(50);
      setJobHistory(jobs);
      const active = jobs.filter((j: PreseedJobSummary) => j.status === 'queued' || j.status === 'processing');
      setActiveJobs(active);
      if (active.length > 0 && !pollRef.current) {
        startPolling();
      } else if (active.length === 0 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err) {
      console.error('Failed to fetch preseed jobs:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchJobs]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = async () => {
      try {
        const jobs = await listPreseedJobs(50);
        setJobHistory(jobs);
        const active = jobs.filter((j: PreseedJobSummary) => j.status === 'queued' || j.status === 'processing');
        setActiveJobs(active);

        if (active.length === 0) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          setSubmitting(false);
          fetchJobs();
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  };

  const handleCancel = async (jobId: number) => {
    setCancellingJobs((prev) => new Set(prev).add(jobId));
    try {
      await cancelPreseedJob(jobId);
      message.info(`Job #${jobId} cancelled`);
      fetchJobs();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e.response?.data?.message || 'Failed to cancel job');
    } finally {
      setCancellingJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  // --- Form row helpers ---
  const updateProduct = (index: number, field: keyof PreseedProduct, value: string) => {
    setProducts((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };
  const addRow = () => setProducts((prev) => [...prev, { mpn: '', manufacturer: '' }]);
  const removeRow = (index: number) => setProducts((prev) => prev.filter((_, i) => i !== index));

  // --- Submit ---
  const handleSubmit = async () => {
    // Merge form rows + bulk text
    const formProducts = products.filter((p) =>
      Object.values(p).some((v) => typeof v === 'string' && v.trim())
    );

    const bulkProducts: PreseedProduct[] = bulkText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/[,\t]+/);
        const item: PreseedProduct = { mpn: parts[0]?.trim() };
        if (parts[1]) item.manufacturer = parts[1].trim();
        if (parts[2]) item.upc = parts[2].trim();
        return item;
      });

    const allProducts = [...formProducts, ...bulkProducts];
    if (allProducts.length === 0) {
      message.warning('Add at least one product');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await submitPreseed(allProducts);
      message.info(`Pre-seed job #${resp.job_id} queued (${resp.total} products)`);
      startPolling();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e.response?.data?.message || e.message || 'Failed to submit');
      setSubmitting(false);
    }
  };

  // --- Active job cards ---
  const renderActiveJobs = () => {
    if (activeJobs.length === 0) return null;
    return (
      <Space direction="vertical" style={{ width: '100%', marginTop: 24 }} size="middle">
        {activeJobs.map((job) => {
          const isRunning = job.status === 'processing';
          const isQueued = job.status === 'queued';
          return (
            <Card
              key={job.job_id}
              size="small"
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    {getStatusIcon(job.status)}
                    <span>Pre-seed Job #{job.job_id}</span>
                    <Tag color={getStatusColor(job.status)}>{job.status.toUpperCase()}</Tag>
                  </Space>
                  <Button
                    danger
                    size="small"
                    icon={<CloseCircleOutlined />}
                    loading={cancellingJobs.has(job.job_id)}
                    onClick={() => handleCancel(job.job_id)}
                  >
                    Cancel
                  </Button>
                </div>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {isQueued ? (
                  <Alert type="info" message="Waiting in queue" description="This job will start once the current job finishes." />
                ) : (
                  <Progress
                    percent={job.progress}
                    status="active"
                    strokeColor={isRunning ? { from: '#108ee9', to: '#87d068' } : undefined}
                  />
                )}
                <Descriptions size="small" column={4} bordered>
                  <Descriptions.Item label="Total"><Text>{job.total}</Text></Descriptions.Item>
                  <Descriptions.Item label="Enriched"><Text type="success">{job.completed}</Text></Descriptions.Item>
                  <Descriptions.Item label="Not Found"><Text type={job.failed > 0 ? 'danger' : 'secondary'}>{job.failed}</Text></Descriptions.Item>
                  <Descriptions.Item label="Remaining"><Text type="secondary">{job.total - job.completed - job.failed}</Text></Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          );
        })}
      </Space>
    );
  };

  // --- History table ---
  const renderHistory = () => {
    const activeJobIds = new Set(activeJobs.map((j) => j.job_id));
    const completed = jobHistory.filter((j) => !activeJobIds.has(j.job_id) && j.status !== 'queued' && j.status !== 'processing');
    if (loadingHistory) return <Card size="small"><Space><LoadingOutlined spin /><Text type="secondary">Loading...</Text></Space></Card>;
    if (completed.length === 0) return <Card size="small"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pre-seed history yet" /></Card>;

    return (
      <Table
        size="small"
        dataSource={completed.map((j) => ({ ...j, key: j.job_id }))}
        pagination={{ pageSize: 5, size: 'small' }}
        columns={[
          { title: 'Job', dataIndex: 'job_id', key: 'job_id', width: 70, render: (id: number) => <Text code>#{id}</Text> },
          { title: 'Client', dataIndex: 'client_name', key: 'client_name', width: 120 },
          { title: 'Status', dataIndex: 'status', key: 'status', width: 120,
            render: (s: string) => <Tag color={getStatusColor(s)} icon={getStatusIcon(s)}>{s.toUpperCase()}</Tag> },
          { title: 'Total', dataIndex: 'total', key: 'total', width: 70 },
          { title: 'Enriched', dataIndex: 'completed', key: 'completed', width: 80,
            render: (v: number) => <Text type="success">{v}</Text> },
          { title: 'Failed', dataIndex: 'failed', key: 'failed', width: 70,
            render: (v: number) => <Text type={v > 0 ? 'danger' : 'secondary'}>{v}</Text> },
          { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 170,
            render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v ? new Date(v).toLocaleString() : '-'}</Text> },
        ]}
      />
    );
  };

  return (
    <div>
      <Title level={3}>Pre-seed Products</Title>
      <Paragraph type="secondary">
        Queue products for background enrichment across all providers (Etilize, IceCat, External).
        Once enriched, follow-on lookups from Odoo or other API clients will be instant cache hits.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        icon={<ExclamationCircleOutlined />}
        style={{ marginBottom: 24 }}
        message="How it works"
        description="Submit product identifiers (MPNs, UPCs, etc.) below. PIM will query all providers in the background, enrich and merge the results, and cache them. When Odoo later requests these products via the /search API, the data is ready immediately."
      />

      {/* Manual entry form */}
      <Card size="small" title="Add Products">
        <Space direction="vertical" style={{ width: '100%' }}>
          {products.map((p, i) => (
            <Space key={i} style={{ width: '100%' }} align="start">
              <Input
                placeholder="MPN"
                value={p.mpn}
                onChange={(e) => updateProduct(i, 'mpn', e.target.value)}
                style={{ width: 180 }}
              />
              <Input
                placeholder="Manufacturer"
                value={p.manufacturer}
                onChange={(e) => updateProduct(i, 'manufacturer', e.target.value)}
                style={{ width: 180 }}
              />
              <Input
                placeholder="UPC (optional)"
                value={p.upc}
                onChange={(e) => updateProduct(i, 'upc', e.target.value)}
                style={{ width: 160 }}
              />
              <Input
                placeholder="Ingram SKU (optional)"
                value={p.ingram_sku}
                onChange={(e) => updateProduct(i, 'ingram_sku', e.target.value)}
                style={{ width: 160 }}
              />
              {products.length > 1 && (
                <Button icon={<DeleteOutlined />} onClick={() => removeRow(i)} danger size="small" />
              )}
            </Space>
          ))}
          <Button type="dashed" onClick={addRow} icon={<PlusOutlined />} style={{ width: 200 }}>
            Add Row
          </Button>

          <Divider orientation="left" plain>Or paste in bulk (MPN, Manufacturer, UPC per line)</Divider>
          <Input.TextArea
            rows={4}
            placeholder={'PA-3260, Palo Alto Networks, 012345678901\nEX4300-48T, Juniper Networks\nSRX380'}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />

          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
            size="large"
          >
            {submitting ? 'Processing...' : 'Start Pre-seed'}
          </Button>
        </Space>
      </Card>

      {renderActiveJobs()}

      <Divider orientation="left">
        <Space>
          <HistoryOutlined />
          Pre-seed History
          <Button type="text" size="small" icon={<ReloadOutlined />} onClick={fetchJobs} loading={loadingHistory} />
        </Space>
      </Divider>
      {renderHistory()}
    </div>
  );
}
